import {
  CreateScheduledEventRequestDto,
  ScheduledEventDto,
  ScheduledEventFormSchema,
  ScheduledEventFormType,
  UpdateScheduledEventRequestDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { FlexContainer, FormBuilder } from '@act/storybook';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { TrackingProvider } from 'react-components/tracking';
import { scheduledEventRequests } from 'redux/requests/scheduled-event.requests';
import {
  createScheduledEventSuccess,
  updateScheduledEventSuccess,
} from 'redux/slices/scheduledEventSlice.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { scheduledEventDateTimeHelpers } from 'utilities/dateTimeGenerator.util';
import { useToast } from '../app/ReusableToast';
import CompositeDialog from '../dialog/CompositeDialog';
import { DialogTitleWithRecoveredNote } from './components/DialogTitleWithRecoveredNote';
import { EventTypeExtended } from './scheduledEventDialogFormTypes';
import useUnsavedChangesDialog from './hooks/useUnsavedChangesDialog.hook';
import { awaitFlushPendingNoteAutosave } from './flushPendingNoteAutosave.util';
import { handleNoteFieldClose } from './noteFieldClose.util';
import { useScheduledEventNoteSaveOverride } from './hooks/useScheduledEventNoteSaveOverride.hook';
import { NoteFieldRef } from './NoteField';
import {
  buildScheduledEventFormConfig,
  SCHEDULED_EVENT_ANYTIME,
  SCHEDULED_EVENT_DATE_FORMAT,
} from './scheduledEventForm.util';

const ANYTIME = SCHEDULED_EVENT_ANYTIME;
export const DATE_FORMAT = SCHEDULED_EVENT_DATE_FORMAT;

const defaultDate = DateTime.now().toFormat(DATE_FORMAT);

export interface ScheduledEventDialogInterfaceProps {
  user: UserDto;
  scheduledEvent?: ScheduledEventDto;
}

interface ScheduledEventDialogProps extends ScheduledEventDialogInterfaceProps {
  handleClose: () => void;
  mode: 'Create' | 'Edit' | 'Next';
  open: boolean;
}

const ScheduledEventDialog = ({
  handleClose,
  mode,
  user,
  scheduledEvent,
  open,
}: ScheduledEventDialogProps) => {
  const { control, handleSubmit, setValue, watch, formState } =
    useForm<ScheduledEventFormType>({
      defaultValues: {
        eventType: scheduledEvent?.type || EventTypeExtended.CALL,
        date: scheduledEvent?.date ? scheduledEvent.date : defaultDate,
        time:
          scheduledEventDateTimeHelpers.getTimeValueFromEpochMillis(
            scheduledEvent?.time
          ) || ANYTIME,
        note: scheduledEvent?.note || '',
        visitLocationId:
          scheduledEvent?.visitLocation &&
          typeof scheduledEvent.visitLocation === 'object' &&
          'id' in scheduledEvent.visitLocation
            ? scheduledEvent.visitLocation.id
            : scheduledEvent && typeof scheduledEvent.visitLocation === 'string'
            ? -1
            : undefined,
        otherLocation:
          scheduledEvent && typeof scheduledEvent.visitLocation === 'string'
            ? scheduledEvent.visitLocation
            : null,
      },
      resolver: zodResolver(ScheduledEventFormSchema),
    });

  const addresses = user?.addresses;
  const visitLocationId = watch('visitLocationId');
  const eventType = watch('eventType');

  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();

  const [
    createdScheduledEvent,
    isCreatingScheduledEvent,
    createScheduledEvent,
  ] = useApiCall(scheduledEventRequests.createScheduledEventForUser);
  const [
    updatedScheduledEvent,
    isUpdatingScheduledEvent,
    updateScheduledEvent,
  ] = useApiCall(scheduledEventRequests.updateScheduledEvent);

  const { scheduledEventNoteSaveOverride } = useScheduledEventNoteSaveOverride({
    userId: user.id,
    scheduledEvent,
  });

  const [showRecoveredNote, setShowRecoveredNote] = useState(false);
  const noteFieldRef = useRef<NoteFieldRef>(null);

  useEffect(() => {
    if (visitLocationId !== -1) setValue('otherLocation', null);
  }, [visitLocationId]);

  const addressOptions = useMemo(() => {
    let returnValue = [];
    const activeAddresses = addresses?.filter(a => !a.deletedAt);
    if (activeAddresses?.length) {
      returnValue = activeAddresses.map(address => {
        return {
          key: address?.id,
          label: `${address?.line1}, ${address?.line2 ?? ''}, ${
            address?.city
          }, ${address?.state} ${address?.zip}`,
          value: address?.id,
        };
      });
    }
    returnValue.push({ key: -1, label: 'Other', value: -1 });
    return returnValue;
  }, [addresses]);

  // Reset location fields when event type changes away from 'visit'
  useEffect(() => {
    if (eventType !== 'visit') {
      setValue('visitLocationId', undefined);
      setValue('otherLocation', null);
    } else if (eventType === 'visit' && !visitLocationId) {
      // When switching to visit, set default to first address if available
      const firstAddressId = addressOptions[0]?.value;
      if (firstAddressId !== undefined && firstAddressId !== -1) {
        setValue('visitLocationId', firstAddressId);
      }
    }
  }, [eventType, visitLocationId, addressOptions, setValue]);

  const timeOptions = useMemo(
    () => [
      { key: ANYTIME, label: 'Anytime', value: ANYTIME },
      ...scheduledEventDateTimeHelpers
        .getTimeOptions(
          scheduledEventDateTimeHelpers.getTimeValueFromEpochMillis(
            scheduledEvent?.time
          )
        )
        .map(option => ({
          key: option.value,
          label: option.label,
          value: option.value,
        })),
    ],
    [scheduledEvent]
  );

  async function handleSave(data: ScheduledEventFormType) {
    if (scheduledEvent) {
      await awaitFlushPendingNoteAutosave(noteFieldRef);
    }

    const dateValue = data.date as string;

    const body:
      | CreateScheduledEventRequestDto
      | UpdateScheduledEventRequestDto = {
      date: dateValue,
      type: data.eventType,
      visitLocationAddressId:
        data.visitLocationId !== -1 ? data.visitLocationId : null,
      visitLocation: data.visitLocationId === -1 ? data.otherLocation : null,
      ...(!!data.note && { note: data.note }),
    };

    // Time is required by Zod validation
    if (data.time === ANYTIME) {
      body.time = null;
    } else {
      const date = DateTime.fromFormat(dateValue, DATE_FORMAT);
      const time = date.plus({ milliseconds: data.time }).toMillis();
      body.time = time;
    }

    try {
      if (scheduledEvent) {
        updateScheduledEvent({
          params: { userId: user.id, scheduledEventId: scheduledEvent.id },
          body,
        });
      } else {
        createScheduledEvent({
          params: { userId: user.id },
          body,
        });
      }
    } catch (err) {
      console.error(`Error saving scheduled event: ${err}`);
    }
  }

  useEffect(() => {
    if (createdScheduledEvent || updatedScheduledEvent) {
      if (createdScheduledEvent) {
        dispatch(createScheduledEventSuccess(createdScheduledEvent));
        noteFieldRef.current?.clearDraft();
        toast('Scheduled event has been created successfully');
        handleClose();
      }
      if (updatedScheduledEvent && !isUpdatingScheduledEvent) {
        dispatch(updateScheduledEventSuccess(updatedScheduledEvent));
        toast('Scheduled event has been updated successfully');
        handleClose();
      }
    }
  }, [createdScheduledEvent, updatedScheduledEvent]);

  const noteMode = scheduledEvent ? 'edit' : 'create';

  const formConfig = useMemo(
    () =>
      buildScheduledEventFormConfig({
        eventType,
        visitLocationId,
        addressOptions,
        timeOptions,
        minDate: DateTime.now(),
        noteFieldRef,
        noteMode,
        userId: user.id,
        scheduledEventId: scheduledEvent?.id,
        noteId: scheduledEvent?.noteId,
        noteSave: scheduledEventNoteSaveOverride,
        includeTrackingNames: true,
        onRecoveredNoteChange: setShowRecoveredNote,
      }),
    [
      eventType,
      visitLocationId,
      addressOptions,
      timeOptions,
      noteMode,
      scheduledEvent?.id,
      scheduledEvent?.noteId,
      scheduledEventNoteSaveOverride,
      user.id,
    ]
  );

  const scheduledEventDialogTitle = `${
    mode === 'Next' ? 'Schedule next event' : 'Schedule event'
  } for ${user.firstName} ${user.lastName}`;

  const dialogTitle = useMemo(
    () => (
      <DialogTitleWithRecoveredNote
        title={scheduledEventDialogTitle}
        showRecoveredNote={showRecoveredNote}
      />
    ),
    [scheduledEventDialogTitle, showRecoveredNote]
  );

  const isSaving = isCreatingScheduledEvent || isUpdatingScheduledEvent;
  const { isDirty } = formState;

  const handleFormClose = () => {
    handleNoteFieldClose({
      mode: scheduledEvent ? 'edit' : 'create',
      isDirty,
      noteFieldRef,
      handleUnsavedChanges,
      finishClose: handleClose,
    });
  };

  return (
    <TrackingProvider contextName="scheduled-event-dialog">
      <CompositeDialog
        handleClose={handleFormClose}
        maxWidth="md"
        open={open}
        title={dialogTitle}
        handleFormSubmit={handleSubmit(data => {
          void handleSave(data);
        })}
        actions={[
          {
            text: 'Cancel',
            callback: () => handleFormClose(),
            variant: 'outlined',
            disabled: isCreatingScheduledEvent || isUpdatingScheduledEvent,
          },
          {
            text: isSaving
              ? 'Saving...'
              : mode === 'Edit'
              ? 'Save'
              : 'Schedule event',
            type: 'submit',
            variant: 'contained',
            isPrimary: true,
            disabled: isSaving,
          },
        ]}
      >
        {mode === 'Next' && (
          <FlexContainer direction="column" padding="16px 0">
            <Typography variant="bodyLarge">
              Please schedule your next interaction with this member.
            </Typography>
          </FlexContainer>
        )}
        <FlexContainer direction="column" padding="16px 0">
          <FormBuilder
            config={formConfig}
            control={control}
            formState={formState}
          />
        </FlexContainer>
      </CompositeDialog>
    </TrackingProvider>
  );
};

export default ScheduledEventDialog;
